// src/services/api/dashboardService.ts

import api from '../../utils/axios'; // ✅ Importar la instancia configurada
import { format } from 'date-fns';

interface SalesMetrics {
  totalSales: number;
  targetPercentage: number;
  dailyTotal: number;
  previousWeekTotal: number;
  previousMonthTotal: number;
  target: number;
  isLoading: boolean;
  error: string | null;
  dailyReturns: number;
  dailyGrossTotal: number;
}

interface VentaResponse {
  informacionVenta: {
    fechaVenta: string;
    estadoVenta: number;
  };
  detalleVenta: {
    total: number;
  };
}

export class DashboardService {
  static async fetchSalesMetrics(sucursalId: number): Promise<SalesMetrics> {
    try {     
      const ventasResponse = await api.post('/ventas/obtener-todas');
      const ventas = ventasResponse.data;
      const today = new Date();
      const todayStr = format(today, 'yyyy-MM-dd');      
    
      let dailyReturnsTotal = 0;
      
      try {
        
        const devolucionesResponse = await api.get(
          '/reportes/devoluciones/ventas',
          {
            params: {
              fechaInicio: todayStr,
              fechaFin: todayStr,
            }
          }
        );       
       
        
        if (devolucionesResponse.data && Array.isArray(devolucionesResponse.data)) {
          // Sumar solo devoluciones procesadas (Estado = true)
          dailyReturnsTotal = devolucionesResponse.data
            .filter((dev: any) => Boolean(dev.Estado))
            .reduce((sum: number, dev: any) => sum + (parseFloat(dev.Monto) || 0), 0);          
          
        }
      } catch (error: any) {
        console.warn('⚠️ No se pudieron obtener las devoluciones del día:', error?.response?.data || error.message);
      }

      // Obtener target de la sucursal
      let target = 50000;
      if (sucursalId) {
        try {
          // ✅ Usar 'api' en lugar de 'axios'
          const sucursalResponse = await api.post(
            `/administracion/sucursales/findOne/${sucursalId}`
          );
          target = parseFloat(sucursalResponse.data?.Target) || target;
        } catch (error) {
          console.warn('No se pudo obtener el target de la sucursal:', error);
        }
      }

      // Filtrar ventas del día (solo pagadas = estado 4)
      const dailyVentas = ventas.filter((v: VentaResponse) => {
        const ventaDateStr = v.informacionVenta.fechaVenta.substring(0, 10)
        return v.informacionVenta.estadoVenta === 4 && ventaDateStr === todayStr;
      });

      // Calcular total bruto del día
      const dailyGrossTotal = dailyVentas.reduce(
        (sum: number, v: VentaResponse) => sum + (v.detalleVenta.total || 0),
        0
      );

      // ✅ Calcular total neto (ventas - devoluciones)
      const dailyTotal = dailyGrossTotal - dailyReturnsTotal;
    

      const targetPercentage = target > 0 ? (dailyTotal / target) * 100 : 0;

      // Semana anterior
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const sevenDaysAgoStr = format(sevenDaysAgo, 'yyyy-MM-dd');

      const previousWeekTotal = ventas
        .filter((v: VentaResponse) => {
          const ventaDateStr = v.informacionVenta.fechaVenta.substring(0, 10);
          return (
            v.informacionVenta.estadoVenta === 4 &&
            ventaDateStr >= sevenDaysAgoStr &&
            ventaDateStr < todayStr
          );
        })
        .reduce((sum: number, v: VentaResponse) => sum + (v.detalleVenta.total || 0), 0);

      // Mes anterior
      const monthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const monthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
      const monthStartStr = format(monthStart, 'yyyy-MM-dd');
      const monthEndStr = format(monthEnd, 'yyyy-MM-dd');

      const previousMonthTotal = ventas
        .filter((v: VentaResponse) => {
          const ventaDateStr = v.informacionVenta.fechaVenta.substring(0, 10);
          return (
            v.informacionVenta.estadoVenta === 4 &&
            ventaDateStr >= monthStartStr &&
            ventaDateStr <= monthEndStr
          );
        })
        .reduce((sum: number, v: VentaResponse) => sum + v.detalleVenta.total, 0);

      return {
        totalSales: dailyVentas.length,
        targetPercentage,
        dailyTotal,
        previousWeekTotal,
        previousMonthTotal,
        target,
        isLoading: false,
        error: null,
        dailyReturns: dailyReturnsTotal,
        dailyGrossTotal: dailyGrossTotal,
      };
    } catch (error) {
      console.error('❌ Error fetching sales metrics:', error);
      return {
        totalSales: 0,
        targetPercentage: 0,
        dailyTotal: 0,
        previousWeekTotal: 0,
        previousMonthTotal: 0,
        target: 50000,
        isLoading: false,
        error: 'Error al cargar las métricas de ventas',
        dailyReturns: 0,
        dailyGrossTotal: 0,
      };
    }
  }

  static async updateSucursalTarget(sucursalId: number, target: number): Promise<void> {
    try {
      // ✅ Usar 'api' en lugar de 'axios'
      await api.post(`/administracion/sucursales/update/${sucursalId}`, {
        Target: target,
      });
    } catch (error) {
      console.error('Error updating target:', error);
      throw new Error('Error al actualizar el objetivo de la sucursal');
    }
  }
}